/**
 * Reconciliation type taxonomy (Requirement 14). FreeAgent exposes a rich set
 * of Money-In / Money-Out explanation types; Ledgiventa maps each onto one of
 * two modes:
 *   - "item"    → settles an outstanding sub-ledger record (service charge / bill)
 *                 via the reconcile path, posting the canonical Settlement.
 *   - "journal" → no sub-ledger item; explained by posting a manual journal to
 *                 a chosen nominal against 1900 Bank (the "Other / journal" path).
 *
 * Transfers (bank-to-bank) need a second account and a different posting, so
 * they are listed but disabled until that path exists.
 */
export type ReconMode = "item" | "journal";
export type MoneyDirection = "in" | "out";

export interface ReconTypeOption {
  key: string;
  label: string;
  direction: MoneyDirection;
  mode: ReconMode;
  /** For `item` mode: which FinancialRecord type the outstanding search queries. */
  recordType?: "service_charge" | "bill";
  enabled: boolean;
}

export const RECON_TYPES: ReconTypeOption[] = [
  // ── Money in ──────────────────────────────────────────────────────────────
  {
    key: "service_charge_receipt",
    label: "Service charge receipt",
    direction: "in",
    mode: "item",
    recordType: "service_charge",
    enabled: true,
  },
  { key: "sales", label: "Sales", direction: "in", mode: "journal", enabled: true },
  { key: "refund_in", label: "Refund", direction: "in", mode: "journal", enabled: true },
  { key: "money_from_user", label: "Money from user", direction: "in", mode: "journal", enabled: true },
  { key: "transfer_in", label: "Transfer from account", direction: "in", mode: "journal", enabled: false },
  { key: "other_in", label: "Other money in", direction: "in", mode: "journal", enabled: true },

  // ── Money out ─────────────────────────────────────────────────────────────
  {
    key: "bill_payment",
    label: "Bill payment",
    direction: "out",
    mode: "item",
    recordType: "bill",
    enabled: true,
  },
  { key: "capital_asset", label: "Purchase of capital asset", direction: "out", mode: "journal", enabled: true },
  { key: "credit_note_refund", label: "Credit note refund", direction: "out", mode: "journal", enabled: true },
  { key: "hp_agreement", label: "Payment of HP agreement", direction: "out", mode: "journal", enabled: true },
  { key: "money_to_user", label: "Money paid to user", direction: "out", mode: "journal", enabled: true },
  { key: "transfer_out", label: "Transfer to account", direction: "out", mode: "journal", enabled: false },
  { key: "other_out", label: "Other money out", direction: "out", mode: "journal", enabled: true },
];

export function typesForDirection(direction: MoneyDirection): ReconTypeOption[] {
  return RECON_TYPES.filter((t) => t.direction === direction);
}

/** The default selected type for a direction: the enabled sub-ledger item type. */
export function defaultTypeForDirection(direction: MoneyDirection): ReconTypeOption {
  const opts = typesForDirection(direction);
  return opts.find((t) => t.mode === "item" && t.enabled) ?? opts[0];
}
